import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Grid, Typography, CircularProgress, Alert, Box, Button } from '@mui/material';
import { fetchFeaturedProducts } from '../../services/productService';
import StyledProductCard from './StyledProductCard';

const PAGE_SIZE = 20;

const FeaturedProducts = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    let ignore = false;

    const loadFeaturedProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchFeaturedProducts(1, PAGE_SIZE);
        if (!ignore) {
          setProducts(data.products || []);
          setPagination(data.pagination || null);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || 'Failed to fetch featured products.');
          setProducts([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadFeaturedProducts();

    return () => {
      ignore = true;
    };
  }, []);

  const handleLoadMore = async () => {
    if (!pagination || loadingMore || pagination.currentPage >= pagination.totalPages) {
      return;
    }

    setLoadingMore(true);
    setError(null);
    try {
      const data = await fetchFeaturedProducts(pagination.currentPage + 1, PAGE_SIZE);
      setProducts((currentProducts) => {
        const productMap = new Map(currentProducts.map((product) => [product.product_id, product]));
        (data.products || []).forEach((product) => productMap.set(product.product_id, product));
        return [...productMap.values()];
      });
      setPagination(data.pagination || pagination);
    } catch (err) {
      setError(err.message || 'Failed to fetch more featured products.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
        {t('home.featuredProducts')}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
         <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert> // Use warning or info
      )}

      {!loading && !error && products.length > 0 && (
        <Grid container spacing={3}>
          {products.map((product, index) => (
            <Grid item key={product.product_id} xs={6} sm={6} md={4} lg={3}>
              <StyledProductCard
                product={product}
                imageLoading={index < 4 ? 'eager' : 'lazy'}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && !error && pagination?.currentPage < pagination?.totalPages && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}

       {!loading && products.length === 0 && !error && (
         <Typography sx={{ mt: 3, textAlign: 'center' }}>
           {t('shop.checkBackSoon')}
         </Typography>
       )}
    </Container>
  );
};

export default FeaturedProducts;
